import { Flex } from '@chakra-ui/react';
import { ButtonText } from '@morressier/alembic';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  endStates,
  IAuthorPhase,
  IEndState
} from '@publishing-engine/domain/models';
import React, { useState } from 'react';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import {
  DecisionMenuActions,
  getModalForDecision,
  IDecisionModal
} from '../../decisionModals/decisionModalHelper';
import { WorkPhaseSection } from './WorkPhaseSection';
import { AdditionalInformation } from '../../AdditionalInformation';
import { EndState } from 'src/components/EndState';
import { SubmissionInfoCard } from 'src/components/SubmissionInfo';
import { IMySubmissionTab } from 'src/constants';
import { getActionLabelAndDescription } from 'src/utils/displayHelpers';
import useAuthAccessQuery from 'src/hooks/useAuthAccessQuery';
interface Props {
  onClose?: () => void;
  setActiveTab: (tab: IMySubmissionTab) => void;
  hasJournalHeader: boolean;
}

export const Sidebar: React.FC<Props> = ({
  onClose,
  setActiveTab,
  hasJournalHeader
}) => {
  const { workflowResponse, submissionId, organizationId } =
    useSubmissionData();

  const { submissionContextId, workflowData, articleWorkflow } =
    workflowResponse;

  const [showingDecisionsForPhase, setShowingDecisionsForPhase] = useState<
    string | null
  >(null);

  const [showingDecisionModal, setShowingDecisionModal] = useState<
    string | null
  >(null);

  const { currentState } = workflowData;
  const transitionGraph = articleWorkflow.transitionGraph;

  const workflowStateConfig = transitionGraph.submission.states[currentState]!;

  let Modal: IDecisionModal | undefined = undefined;
  if (showingDecisionModal) {
    Modal = getModalForDecision(showingDecisionModal as DecisionMenuActions);
  }

  const isEndState = endStates.includes(workflowData.currentState as IEndState);
  const { isAllowed } = useAuthAccessQuery({
    relation: 'author',
    entityType: 'submission',
    entityId: submissionId
  });
  const endStateActions = isAllowed
    ? workflowStateConfig.actions
        .filter(
          actionReference =>
            actionReference.displayProperties.location === 'contextMenu'
        )
        .map(actionReference => {
          const actionData = getActionLabelAndDescription(actionReference.name);

          return {
            name: actionReference.name,
            action: () => setShowingDecisionModal(actionReference.name),
            label: actionData?.label || actionReference.name,
            description: actionData?.description ?? '',
            headingColor: actionData?.headingColor
          };
        })
    : [];

  return (
    <>
      {/* ----- Decision Modal ----- */}
      {showingDecisionModal && Modal ? (
        <Modal
          submissionId={submissionId}
          organizationId={organizationId}
          submissionContextId={submissionContextId}
          onClose={() => {
            setShowingDecisionModal(null);
            setShowingDecisionsForPhase(null);
          }}
          setActiveTab={setActiveTab}
        />
      ) : null}

      <Flex width="100%" flexDirection="column">
        {/* ----- Close Button ----- */}
        {onClose ? (
          <Flex
            paddingX={8}
            marginTop={4}
            alignItems="center"
            flexDirection="row"
          >
            <ButtonText
              leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
              onClick={onClose}
            >
              All Submissions
            </ButtonText>
          </Flex>
        ) : null}

        <Flex flexDirection="column" gap={4} padding={4}>
          <>
            {!hasJournalHeader && <SubmissionInfoCard />}
            <Flex direction={'column'} gap={4}>
              {isEndState ? (
                <EndState
                  role="author"
                  currentWorkflowState={workflowData.currentState as IEndState}
                  dateEnteredCurrentPhase={workflowData.dateEnteredCurrentPhase}
                  actions={endStateActions}
                />
              ) : (
                <WorkPhaseSection
                  key={currentState}
                  showingDecisionsForPhase={showingDecisionsForPhase}
                  setShowingDecisionsForPhase={setShowingDecisionsForPhase}
                  phaseName={currentState as IAuthorPhase}
                  workflowStateConfig={workflowStateConfig}
                  submissionContextId={submissionContextId}
                  setActiveTab={setActiveTab}
                  setShowingDecisionOptions={setShowingDecisionModal}
                />
              )}
              <AdditionalInformation
                role="author"
                setActiveTab={setActiveTab}
                workflowResponse={workflowResponse}
              />
            </Flex>
          </>
        </Flex>
      </Flex>
    </>
  );
};
